import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ScopeVersion } from '@core/model/scope-version'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { User } from '@app/core/model/user.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { CommonModule } from '@angular/common'
import { SharedModule } from '@app/shared/shared.module'
import { ScopeUiTableComponent } from '@app/shared/components/ui-components/scope-ui-table/scope-ui-table.component'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Role } from '@app/features/scoping/models/role.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { Store } from '@ngrx/store'
import { Preference } from '@core/model/user-preferences.interface'
import { BootstrapActions } from '@core/store'
import { cloneDeep } from 'lodash'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { formatDisplayHoursToMinutes, formatHours, trackById } from '@shared/utils/utils'
import { CdnConfig } from '@core/model/cdn-config.model'
import { ScopeElementsModule } from '@app/shared/components/scope-elements/scope-elements.module'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { ExchangeRateService } from '@app/shared/services/exchange-rate.service'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { MatDialog } from '@angular/material/dialog'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { LanguageService } from '@app/core/service/language.service'
import { FormControl } from '@angular/forms'
import { plainToInstance } from 'class-transformer'
import { ScopeOverviewService } from '@app/features/scope-overview/service/scope-overview.service'
import { SnackbarService } from '@app/shared/utils/snackbar.service'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { AuthService } from '@app/core/service/auth.service'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import {
  ScopeElementsConfig,
  UpdateDeliverablePayload,
  UpdateFeeItemPayload,
  UpdateRolePayload,
  UpdateTpcPayload,
} from '@app/shared/components/scope-elements/models'
import { MappedEntity } from '@app/features/scoping/models/mapped-entity.model'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { ScopeRetainedHoursStats } from '@app/features/scope-overview/model/scope-retained-hours-stats.model'
import { ScopeTableComponent } from '../../scope-tab/scope-table/scope-table.component'
import { UpdateComponentPayload } from '@app/shared/components/scope-elements/models/update-component-payload.interface'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { MoveDeliverablePayload } from '@app/features/scope-overview/model/move-deliverable.payload.model'
import {
  getCrFieldToDisplay,
  getCrNetChanges,
  showCrDifferences,
} from '@app/shared/components/scope-elements/utils/change-request'
import { ScopeWithChanges } from '@app/shared/components/scope-elements/models/entities-with-changes/scope-with-changes.interface'
import { formatCrPrice, formatPercentage } from '@app/shared/components/scope-elements/utils/format-value.utils'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { FormatHoursPipe, formatHoursPipeFn } from '@app/shared/pipe/formatHours.pipe'
import { Money } from '@app/features/scope-overview/model/money.model'
import { DataMappingService } from '@app/features/scoping/service/data-mapping.service'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'

@Component({
  selector: 'scope-by-work-table',
  imports: [CommonModule, SharedModule, ScopeUiTableComponent, ScopeElementsModule, ScopeTableComponent],
  templateUrl: './scope-by-work-table.component.html',
  styleUrls: ['./scope-by-work-table.component.scss'],
  standalone: true,
  providers: [FormatHoursPipe],
})
export class ScopeByWorkTableComponent implements OnChanges {
  @Input() isTimeline = false
  @Input() isEditable?: boolean
  @Input() scopeVersion!: ScopeWithChanges
  @Input() currentRatecard: RatecardVersion
  @Input() sortedSections: ScopeSection[]
  @Input() sortedDefaultSectionDeliverables: Deliverable[]
  @Input() cdnConfig?: CdnConfig
  @Input() scopeRetainedHoursStats?: ScopeRetainedHoursStats
  @Input() showUseRetainedTeamToggle?: boolean
  // Column classes
  @Input() mainColumnClass: string
  @Input() componentMainColumnClass: string
  @Input() roleMainColumnClass: string
  // Menu options
  @Input() deliverableMenuOptions: MenuOptions[]
  @Input() componentMenuOptions: MenuOptions[]
  @Input() feeMenuOptions!: MenuOptions[]
  // Change Request
  @Input() currentChangeRequest: ChangeRequest
  @Input() showCRDifferences: boolean
  // Net Changes
  @Input() showNetChanges: boolean

  @Output() onFetchDeliverable = new EventEmitter<number>()
  @Output() onUpdateDeliverable = new EventEmitter<UpdateDeliverablePayload>()
  @Output() onUpdateComponent = new EventEmitter<UpdateComponentPayload>()
  @Output() onAcceptDeliverable = new EventEmitter<Deliverable>()
  @Output() onRejectDeliverable = new EventEmitter<Deliverable>()
  @Output() onDuplicateDeliverable = new EventEmitter<Deliverable>()
  @Output() onDeleteDeliverable = new EventEmitter<Deliverable>()
  @Output() onAddDeliverableFee = new EventEmitter<Deliverable>()
  @Output() onAddComponentFee = new EventEmitter<ScopeComponent>()
  @Output() onDeleteComponent = new EventEmitter<ScopeComponent>()
  @Output() onCompleteTrade = new EventEmitter<Deliverable>()
  @Output() onTradeDeliverable = new EventEmitter<Deliverable>()
  @Output() onStopProgressTrade = new EventEmitter<Deliverable>()
  @Output() onAddDeliverableToTrade = new EventEmitter<Deliverable>()
  @Output() onConfirmTrade = new EventEmitter<Deliverable>()
  @Output() onCancelTrade = new EventEmitter<Deliverable>()
  @Output() onReopenTrade = new EventEmitter<Deliverable>()
  @Output() onShowDeliverableApplyLocationCardDialog = new EventEmitter<Deliverable>()
  @Output() onUpdateFeeItem = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onAddSectionFee = new EventEmitter<ScopeSection>()
  @Output() onDeleteSection = new EventEmitter<ScopeSection>()
  @Output() onMoveDeliverable = new EventEmitter<MoveDeliverablePayload>()
  @Output() onMoveComponent = new EventEmitter<any>()
  @Output() onMoveComponentOutsideDeliverable = new EventEmitter<ScopeComponent>()
  @Output() onAddScopeFee = new EventEmitter<void>()
  @Output() onAddDiscount = new EventEmitter<void>()
  @Output() onEditDiscount = new EventEmitter<void>()
  @Output() onDeleteDiscount = new EventEmitter<void>()
  @Output() onEditMsaLineItem = new EventEmitter<void>()
  @Output() onDeleteMsaLineItem = new EventEmitter<void>()
  @Output() onUpdateUseRetainedHours = new EventEmitter<any>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()

  get currentUser(): User {
    return this.authService.loggedInUser
  }

  isScopeValuesLoading$ = this.store.select(ScopeOverviewSelectors.selectCurrentScopeValuesLoading)

  scopeFees: MappedEntity<FeeItemInstance>[] = []

  constructor(
    public scopeApprovalFlowService: ApprovalFlowService,
    public scopeTabService: ScopeTabService,
    protected scopeElementsService: ScopeElementsService,
    private store: Store,
    private authService: AuthService,
    protected exchangeRateService: ExchangeRateService,
    private dialog: MatDialog,
    private lang: LanguageService,
    private scopeOverviewService: ScopeOverviewService,
    private snackbarService: SnackbarService,
    private convertCurrencyPipe: ConvertCurrencyPipe,
    public mappingService: DataMappingService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scopeVersion) {
      this.scopeFees = this.mappingService.transformArray<FeeItemInstance>(
        this.scopeVersion?.feeItems || [],
        this.scopeElementsService.scopeFeeColumns
      )
    }
  }

  showDeliverableApplyLocationCardDialog(deliverable: Deliverable) {
    this.onShowDeliverableApplyLocationCardDialog.emit(deliverable)
  }

  updateFeeItem(payload: UpdateFeeItemPayload | FeeItemInstance) {
    this.onUpdateFeeItem.emit({
      feeItem: payload as FeeItemInstance,
      section: (payload as UpdateFeeItemPayload).section,
      deliverable: (payload as UpdateFeeItemPayload).deliverable,
      component: (payload as UpdateFeeItemPayload).component,
    })
  }

  updateDeliverableTpc({ deliverable, thirdPartyCost }: UpdateTpcPayload) {
    this.scopeOverviewService
      .updateDeliverableThirdPartyCost(this.scopeVersion.identity.id, deliverable.id, thirdPartyCost)
      .subscribe({
        next: () =>
          this.store.dispatch(ScopeOverviewActions.updateDeliverableTpcSuccess({ deliverable, thirdPartyCost })),
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
  }

  updateComponentTpc({ component, deliverable, thirdPartyCost }: UpdateTpcPayload) {
    this.scopeOverviewService
      .updateComponentThirdPartyCost(this.scopeVersion.identity.id, component.id, thirdPartyCost)
      .subscribe({
        next: () =>
          this.store.dispatch(
            ScopeOverviewActions.updateComponentTpcSuccess({ component, deliverable, thirdPartyCost })
          ),
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
  }

  updateDeliverableSectionTpc({ deliverable, section, thirdPartyCost }: UpdateTpcPayload) {
    this.scopeOverviewService
      .updateDeliverableSectionThirdPartyCost(this.scopeVersion.identity.id, deliverable.id, section.id, thirdPartyCost)
      .subscribe({
        next: () =>
          this.store.dispatch(
            ScopeOverviewActions.updateDeliverableSectionTpcSuccess({ deliverable, section, thirdPartyCost })
          ),
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
  }

  reorderDeliverableTpcs({ deliverable, thirdPartyCosts }) {
    thirdPartyCosts.forEach((tpc, index) => (tpc.order = index))
    this.scopeOverviewService
      .reorderDeliverableThirdPartyCosts(this.scopeVersion.identity.id, deliverable.id, thirdPartyCosts)
      .subscribe({
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
        },
      })
  }

  reorderComponentTpcs({ component, thirdPartyCosts }) {
    thirdPartyCosts.forEach((tpc, index) => (tpc.order = index))
    this.scopeOverviewService
      .reorderComponentThirdPartyCosts(this.scopeVersion.identity.id, component.id, thirdPartyCosts)
      .subscribe({
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
        },
      })
  }

  reorderDeliverableSectionTpcs({ deliverable, section, thirdPartyCosts }) {
    thirdPartyCosts.forEach((tpc, index) => (tpc.order = index))
    this.scopeOverviewService
      .reorderDeliverableSectionThirdPartyCosts(
        this.scopeVersion.identity.id,
        deliverable.id,
        section.id,
        thirdPartyCosts
      )
      .subscribe({
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
        },
      })
  }

  updateRole({ component, role }: UpdateRolePayload) {
    this.scopeOverviewService.updateComponentRole(this.scopeVersion.identity.id, component.id, role, false).subscribe({
      next: () => this.store.dispatch(ScopeOverviewActions.overrideRoleSuccess({ component, role })),
      error: (error) => {
        this.snackbarService.showDefaultErrorSnackbar()
        console.error(error)
        return error
      },
    })
  }

  overrideRateCardRoleHours({ component, role }: UpdateRolePayload) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Change Role Hours`,
        `Enter the new hours for ${role.name}`,
        'Confirm change',
        undefined,
        (form: FormControl) => {
          dialog.close()
          const minutes = formatDisplayHoursToMinutes(form.get('rateCardMinutes')?.value)
          const updatedRole = plainToInstance(Role, { ...role, rateCardMinutes: minutes })

          this.scopeOverviewService
            .updateComponentRole(this.scopeVersion.identity.id, component.id, updatedRole, true)
            .subscribe({
              next: () => {
                this.store.dispatch(
                  ScopeOverviewActions.overrideRoleSuccess({ component: component, role: updatedRole })
                )
              },
              error: (error) => {
                this.snackbarService.showDefaultErrorSnackbar()
                console.error(error)
                return error
              },
            })
        },
        undefined,
        [
          {
            name: 'rateCardMinutes',
            control: new FormControl(formatHours(role.rateCardMinutes / 60)),
            type: this.authService.loggedInUser.company.hasApplicationSetting(
              ApplicationSetting.SCOPE__AGENCY_HOURS_AND_MINUTES
            )
              ? 'hoursMinutes'
              : 'hours',
            label: `${this.lang.get('agency')} Hours`,
          },
        ]
      ),
    })
  }

  onToggleScopePreference(preference: Preference) {
    this.store.dispatch(BootstrapActions.updateScopeOverviewColumnPreferences({ updatedPrefs: preference }))
  }

  dropSection(event: CdkDragDrop<any>) {
    const sections = cloneDeep(event.container.data)
    moveItemInArray(sections, event.previousIndex, event.currentIndex)
    sections.forEach((d, i) => (d.order = i))
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSectionsOrder({ scopeId: this.scopeVersion.identity.id, sections: sections })
    )
  }

  get sectionDrops() {
    const str: string[] = []
    this.scopeVersion.sections.forEach((s) => {
      str.push('list-' + s.id)
    })
    str.push('list-one')
    return str
  }

  getDiscountValue(scopeVersion: ScopeVersion) {
    return scopeVersion.discount.getType() == 'PERCENTAGE'
      ? scopeVersion.discount.getValueByPercentage(
          scopeVersion.getTotalSellingPrice({ excludeFees: false, excludeMsa: true, excludeDiscount: true })?.getValue()
        )
      : scopeVersion.discount.getValueFormatted()
  }

  addScopeFee = () => this.onAddScopeFee.emit()
  addDiscount = () => this.onAddDiscount.emit()
  editDiscount = () => this.onEditDiscount.emit()
  deleteDiscount = () => this.onDeleteDiscount.emit()
  editMsaLineItem = () => this.onEditMsaLineItem.emit()
  deleteMsaLineItem = () => this.onDeleteMsaLineItem.emit()
  isUseRetainedHours = () => this.scopeVersion.useRetainedHours

  updateUseRetainedHours($event: any) {
    this.onUpdateUseRetainedHours.emit($event)
  }

  getTotalDeliverableBudget() {
    if (this.currentChangeRequest && this.scopeVersion.changes?.deliverableBudgetTotal) {
      return this.scopeVersion.changes.deliverableBudgetTotal.new
    }
    return this.scopeVersion.getTotalDeliverableBudget()?.amount
  }

  getBudgetBalance() {
    if (this.currentChangeRequest && this.scopeVersion.changes?.budgetBalance) {
      return this.scopeVersion.changes.budgetBalance.new
    }
    return this.scopeVersion.budgetBalance?.amount
  }

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay(fieldName, this.scopeVersion)
  showDiff = (fieldName: string): boolean =>
    showCrDifferences(
      {
        isChangeRequest: !!this.currentChangeRequest,
        showDiff: this.showCRDifferences,
      } as ScopeElementsConfig,
      this.scopeVersion,
      fieldName
    )

  formatPrice = (value: number | Money): string =>
    formatCrPrice(value, this.exchangeRateService.selectedCurrency, this.convertCurrencyPipe)
  protected readonly formatHours = formatHoursPipeFn

  getNetChanges = (fieldName: string, defaultValue: number): number =>
    getCrNetChanges(this.scopeVersion.changes, fieldName, defaultValue)

  protected readonly trackById = trackById
  protected readonly Privilege = Privilege
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly formatPercentage = formatPercentage
}
